import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FamilyPlazaSubHeading from '../../../components/family-plaza/FamilyPlazaSubHeading';
import FamilyPlazaTopSection from '../../../components/family-plaza/FamilyPlazaTopSection';
import { AncestralTabService } from '../../../services';
import PrintPreviewModal from '../../../components/family-plaza/PrintPreviewModal';

const AncestralTabletMobile = () => {
  const navigate = useNavigate();
  const [selectedAncestralSubject, setSelectedAncestralSubject] = useState('고조부모');
  const [inputValue, setInputValue] = useState('');
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [orientation, setOrientation] = useState('landscape'); // 기본값: landscape

  const handleAncestralSubjectChange = (subject) => {
    setSelectedAncestralSubject(subject);
  };

  const handleTextInputChange = (value) => {
    setInputValue(value);
  };

  const handleLastNameCheck = async () => {
    const lastname = inputValue;
    try {
      const response = await AncestralTabService.printCheck({
        lastname: lastname
      });
      console.log('PC Ancestral Tablet Lastname Check Response:', response);
      alert(response.message);
    } catch (error) {
      console.log('API error:', error);
      alert(error.message);
    }
  };


  const handlePrintAncestralTablet = async () => {
    // Get form values using JSP logic
    const goin = getSelectedRelationship();
    const position = getSelectedPosition();
    const lastname = getSelectedLastname();
    const sex = determineGender(goin, position, lastname);

    // Debug logging
    console.log('Debug values:', { goin, position, lastname, sex });

    // Create output data
    const outputData = {
      goin: goin,
      position: position,
      lastname: lastname,
      sex: sex,
      timestamp: new Date().toISOString()
    };

    console.log('Mobile Ancestral Tablet Output Data:', outputData);

    try {
      const response = await AncestralTabService.printProcess({
        goin: goin,
        position: position,
        lastname: lastname
      });
      console.log('PC Ancestral Tablet Response:', response);

      if (response) {
        setShowPrintPreview(true);
        // Prepare print data outside of setTimeout to reduce handler execution time
        const printData = {
          goin: response.goin,
          position: response.positionHanja,
          lastname: response.lastnameHanja
        };
        
        setTimeout(() => {
          setShowPrintPreview(false);
          // Use requestAnimationFrame to defer heavy DOM operations
          requestAnimationFrame(() => {
            handlePrint(printData.goin, printData.position, printData.lastname);
          });
        }, 50);
      }
    } catch (error) {
      console.error('Error processing print request:', error);
      alert('지방 출력 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  // Helper functions to match JSP logic
  const getSelectedRelationship = () => {
    // Use React state instead of querying DOM
    if (!selectedAncestralSubject) return '0|1'; // Default to 고조부모

    // Map Korean values to JSP values
    const relationshipMap = {
      '고조부모': '0|1',
      '고조부': '0',
      '고조모': '1',
      '증조부모': '2|3',
      '증조부': '2',
      '증조모': '3',
      '조부모': '4|5',
      '조부': '4',
      '조모': '5',
      '백부모': '6|7',
      '백부': '6',
      '백모': '7',
      '부모': '8|9',
      '부': '8',
      '모': '9',
      '처': '10',
      '남편': '11',
      '제수': '12',
      '동생': '13',
      '형수': '14',
      '형': '15'
    };

    return relationshipMap[selectedAncestralSubject] || '0|1';
  };

  const getSelectedPosition = () => {
    // For mobile, we'll use a default position since it's not directly mapped
    return '학생'; // Default to 학생
  };

  const getSelectedLastname = () => {
    return inputValue ? inputValue.trim() : ''; 
  };

  const determineGender = (goin, position, lastname) => {
    if (!goin) return 'MF'; // Default to couple

    // Check if it's a couple (contains |)
    if (goin.includes('|')) {
      return 'MF'; // Male and Female
    }

    // Single person - determine gender based on JSP logic
    const maleRelationships = ['0', '2', '4', '6', '8', '11', '15'];
    const femaleRelationships = ['1', '3', '5', '7', '9', '10', '12', '14'];

    if (maleRelationships.includes(goin)) {
      return 'M';
    } else if (femaleRelationships.includes(goin)) {
      return 'F';
    } else if (goin === '13') { // 동생 - ambiguous
      return position ? 'M' : 'F';
    }

    return 'MF'; // Default to couple
  };

  const handlePrint = (goin, position, lastname) => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=920,height=640,scrollbars=yes,resizable=yes');

    if (printWindow) {
      // Pre-generate tablet content to avoid doing it in the setTimeout
      const tabletContent = generateTablets(goin, position, lastname);
      
      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
          <title>지방출력 | 파라다이스 추모원</title>
          <style>
            @page {
              size: A4 ${orientation};
              margin: 0.5in 0.2in 0.5in 0.3in;
            }
            @media print {
              body { 
                margin: 0;
                padding: 0;
                width: 100%;
                height: 100%;
                overflow: hidden;
              }
              .no-print { display: none !important; }
              * {
                page-break-inside: avoid;
                page-break-after: avoid;
              }
              div[style*="float:left"] {
                page-break-inside: avoid;
                page-break-after: avoid;
              }
            }
          </style>
        </head>
        <body bgcolor="#FFFFFF" text="#000000" leftmargin="0" topmargin="0" marginwidth="0" marginheight="0" align="center">
          <div style="width:100%; height:100%; text-align:center; page-break-inside:avoid; overflow:hidden; padding-left:5px;">
            ${tabletContent}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
        </html>
      `;

      printWindow.document.write(printHTML);
      printWindow.document.close();
    } else {
      alert('팝업이 차단되었습니다. 팝업을 허용해주세요.');
    }
  };

  const generateTablets = (goin, position, lastname) => {
    // Pre-generate tablet content once to avoid repeated calls
    const tabletContent = generateTabletContent(goin, position, lastname);
    
    // Use array join for better performance than string concatenation
    const tablets = [];
    const tabletTemplate = `
        <div style="float:left; margin:0 1px 0 0; position:relative; font-family:'바탕체';width:148px; height:580px;">
          <img src="https://www.prds.kr/images/popup_jibang_bg.png" style="position:absolute; top:0; left:0; width:148px; height:580px;" />
          <div style="position:absolute; top:0; left:0; font-size:42px;font-family:'바탕체'; width:148px; height:580px;">
            ${tabletContent}
          </div>
        </div>
      `;

    // Generate 5 tablets
    for (let i = 0; i < 5; i++) {
      tablets.push(tabletTemplate);
    }

    return tablets.join('');
  };

  const generateTabletContent = (goin, position, lastname) => {
    // Exact logic for generating tablet content with conditional line breaks
    const hasLastName = lastname && lastname.trim() !== '';
    const femaleNameBreaks = hasLastName ? '' : '<br><br><br><br>';

    if (goin === '0|1') { // 고조부모
      return `
        <div style="position:absolute; top:25px; left:26px;font-family:'바탕체'; width:39px;  line-height:55px;">顯高祖考${position}府君</div>
        <div style="position:absolute; top:29px; left:82px;font-family:'바탕체'; width:39px;  line-height:45px;">顯高祖妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:474px; left:26px;font-family:'바탕체'; width:30px; line-height:46px;">神位</div>
        <div style="position:absolute; top:474px; left:82px;font-family:'바탕체'; width:30px; line-height:46px;">神位</div>
      `;
    } else if (goin === '0') { // 고조부
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:55px;">顯高祖考${position}府君</div>
        <div style="position:absolute; top:469px; left:52px;font-family:'바탕체'; width:30px; line-height:55px;">神位</div>
      `;
    } else if (goin === '1') { // 고조모
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:46.0px;">顯高祖妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:474px; left:52px;font-family:'바탕체'; width:30px; line-height:46px;">神位</div>
      `;
    } else if (goin === '2|3') { // 증조부모
      return `
        <div style="position:absolute; top:25px; left:26px;font-family:'바탕체'; width:39px;  line-height:50px;">顯曾祖考${position}府君</div>
        <div style="position:absolute; top:29px; left:82px;font-family:'바탕체'; width:39px;  line-height:45px;">顯曾祖妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:474px; left:26px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
        <div style="position:absolute; top:474px; left:82px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '2') { // 증조부
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:55px;">顯曾祖考${position}府君</div>
        <div style="position:absolute; top:460px; left:52px;font-family:'바탕체'; width:30px; line-height:55px;">神位</div>
      `;
    } else if (goin === '3') { // 증조모
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:45px;">顯曾祖妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:469px; left:52px;font-family:'바탕체'; width:30px; line-height:45px;">神位</div>
      `;
    } else if (goin === '4|5') { // 조부모
      return `
        <div style="position:absolute; top:25px; left:26px;font-family:'바탕체'; width:39px;  line-height:60px;">顯祖考${position}府君</div>
        <div style="position:absolute; top:29px; left:82px;font-family:'바탕체'; width:39px;  line-height:50px;">顯祖妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:474px; left:26px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
        <div style="position:absolute; top:474px; left:82px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '4') { // 조부
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:60px;">顯祖考${position}府君</div>
        <div style="position:absolute; top:450px; left:52px;font-family:'바탕체'; width:30px; line-height:60px;">神位</div>
      `;
    } else if (goin === '5') { // 조모
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:50px;">顯祖妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:464px; left:52px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '6|7') { // 백부모
      return `
        <div style="position:absolute; top:25px; left:26px;font-family:'바탕체'; width:39px;  line-height:60px;">顯伯父${position}府君</div>
        <div style="position:absolute; top:29px; left:82px;font-family:'바탕체'; width:39px;  line-height:50px;">顯伯母孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:469px; left:26px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
        <div style="position:absolute; top:469px; left:82px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '6') { // 백부
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:60px;">顯伯父${position}府君</div>
        <div style="position:absolute; top:450px; left:52px;font-family:'바탕체'; width:30px; line-height:60px;">神位</div>
      `;
    } else if (goin === '7') { // 백모
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:50px;">顯伯母孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:469px; left:52px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '8|9') { // 부모
      return `
        <div style="position:absolute; top:25px; left:26px;font-family:'바탕체'; width:39px;  line-height:65px;">顯考${position}府君</div>
        <div style="position:absolute; top:29px; left:82px;font-family:'바탕체'; width:39px;  line-height:55px;">顯妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:469px; left:26px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
        <div style="position:absolute; top:469px; left:82px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '8') { // 부
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:65px;">顯考${position}府君</div>
        <div style="position:absolute; top:440px; left:52px;font-family:'바탕체'; width:30px; line-height:65px;">神位</div>
      `;
    } else if (goin === '9') { // 모
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:55px;">顯妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:460px; left:52px;font-family:'바탕체'; width:30px; line-height:55px;">神位</div>
      `;
    } else if (goin === '11') { // 남편
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:65px;">顯辟${position}府君</div>
        <div style="position:absolute; top:440px; left:52px;font-family:'바탕체'; width:30px; line-height:65px;">神位</div>
      `;
    } else if (goin === '10') { // 처
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:55px;">亡室孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:450px; left:52px;font-family:'바탕체'; width:30px; line-height:55px;">神位</div>
      `;
    } else if (goin === '15') { // 형
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:65px;">顯兄${position}府君</div>
        <div style="position:absolute; top:440px; left:52px;font-family:'바탕체'; width:30px; line-height:65px;">神位</div>
      `;
    } else if (goin === '14') { // 형수
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:50px;">顯兄嫂孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:469px; left:52px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '13') { // 동생
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:65px;">亡弟${position}府君</div>
        <div style="position:absolute; top:440px; left:52px;font-family:'바탕체'; width:30px; line-height:65px;">神位</div>
      `;
    } else if (goin === '12') { // 제수
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:50px;">亡弟嫂孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:464px; left:52px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    }

    return '';
  };

  return (
    <div className="announcement-list-page cyber-info-page-pc">

      <FamilyPlazaTopSection />

      <main className="main-content">

        <FamilyPlazaSubHeading heading="지방출력" />

        {/* Content Section */}
        <div className="ancestral-tablet-mobile-content-unique">
          <div className="ancestral-tablet-mobile-content-container-unique">
            {/* Ancestral Rite Subject Selection */}
            <div className="ancestral-tablet-mobile-main-content-unique">
              <h3 className="ancestral-tablet-mobile-content-title-unique">제사대상</h3>

              <div className="ancestral-tablet-mobile-subject-grid-unique">
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '고조부모' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('고조부모')}
                >
                  고조부모
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '증조부모' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('증조부모')}
                >
                  증조부모
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '조부모' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('조부모')}
                >
                  조부모
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '백부모' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('백부모')}
                >
                  백부모
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '부모' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('부모')}
                >
                  부모
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '남편' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('남편')}
                >
                  남편
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '형수' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('형수')}
                >
                  형수
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '고조부' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('고조부')}
                >
                  고조부
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '증조부' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('증조부')}
                >
                  증조부
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '조부' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('조부')}
                >
                  조부
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '백부' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('백부')}
                >
                  백부
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '부' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('부')}
                >
                  부
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '처' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('처')}
                >
                  처
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '동생' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('동생')}
                >
                  동생
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '고조모' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('고조모')}
                >
                  고조모
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '증조모' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('증조모')}
                >
                  증조모
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '조모' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('조모')}
                >
                  조모
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '백모' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('백모')}
                >
                  백모
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '모' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('모')}
                >
                  모
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '형' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('형')}
                >
                  형
                </button>
                <button
                  className={`ancestral-tablet-mobile-subject-button-unique ${selectedAncestralSubject === '제수' ? 'ancestral-tablet-mobile-subject-active-unique' : ''}`}
                  onClick={() => handleAncestralSubjectChange('제수')}
                >
                  제수
                </button>
              </div>
            </div>

            {/* Gender Selection Section - Conditional Rendering */}
            {/* First Option - 고조부모 (Both Male and Female) - matches great-great-grandparents */}
            {selectedAncestralSubject === '고조부모' && (
              <div className="ancestral-tablet-mobile-gender-section-unique">
                <div className="ancestral-tablet-mobile-text-container-unique">
                  <h3 className="ancestral-tablet-mobile-content-title-unique">제사 대상 성별</h3>
                </div>

                <div className="ancestral-tablet-mobile-info-box-unique">
                  <h4 className="ancestral-tablet-mobile-info-title-unique">남성</h4>
                  <p className="ancestral-tablet-mobile-info-text-unique">
                    남자의 지방에는 제사 대상의 관직명이 표시됩니다. <br /> 아래에서 선택한 광직명이 선택하신 기본 양식에 적용되어 출력됩니다.
                  </p>
                </div>

                <div className="ancestral-tablet-mobile-radio-options-unique">
                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title"
                      value="student"
                      className="ancestral-tablet-mobile-radio-input-unique"
                      defaultChecked
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      학생 - 일반적으로 가장 널리 쓰임
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title"
                      value="chairman"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      회장 - 법인/단체의 장(00협회장,00회장 등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title"
                      value="military"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      군관 - 군인의 경우 사용(장군, 고위장교 등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title"
                      value="professional"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      처사 - 전문직 종사자(회계사, 변리사등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title"
                      value="judge"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      법관 - 법조인의 경우 사용(판, 검사, 변호사 등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title"
                      value="executive"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      임원 - 대기업 고위직의 경우(대기업 회장, 사장, 간부 등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title"
                      value="legislator"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      의원 - 정치인(국회의원, 자치단체의원)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title"
                      value="scholar"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      학자 - 학계에 계셨던 분(교수, 박사, 학자 등)
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Female section for first option (고조부모) */}
            {selectedAncestralSubject === '고조부모' && (
              <div className="ancestral-tablet-mobile-female-section-unique">
                <div className="ancestral-tablet-mobile-info-box-unique">
                  <h4 className="ancestral-tablet-mobile-info-title-unique">여성</h4>
                  <p className="ancestral-tablet-mobile-info-text-unique">
                    제사 대상이 여성일 경우 본관과 성씨가 입력됩니다. <br /> 본관 성씨를 빈칸에 한글로 적으시면 선택하신 기본양식에 적용되어 출력됩니다.
                  </p>
                </div>

                <div className="ancestral-tablet-mobile-female-input-section-unique">
                  <label className="ancestral-tablet-mobile-female-radio-option-unique">
                    <input
                      type="radio"
                      name="female-input"
                      value="bon-gwan"
                      className="ancestral-tablet-mobile-female-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-female-radio-label-unique">
                      본관 성씨 입력
                    </span>
                  </label>

                  <div className="ancestral-tablet-mobile-female-input-container-unique">
                    <input
                      type="text"
                      className="ancestral-tablet-mobile-female-input-field-unique"
                      placeholder="예) 김해김, 경주김"
                      value={inputValue}
                      onChange={(e) => handleTextInputChange(e.target.value)}
                    />
                    <button className="ancestral-tablet-mobile-female-search-button-unique" onClick={() => handleLastNameCheck()}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Male-only subjects - matches great-great-grandfather, great-grandfather, grandfather, granduncle, father, husband, elder-brother */}
            {['고조부', '증조부', '조부', '백부', '부', '남편', '형'].includes(selectedAncestralSubject) && (
              <div className="ancestral-tablet-mobile-gender-section-unique">
                <div className="ancestral-tablet-mobile-text-container-unique">
                  <h3 className="ancestral-tablet-mobile-content-title-unique">제사 대상 성별</h3>
                </div>

                <div className="ancestral-tablet-mobile-info-box-unique">
                  <h4 className="ancestral-tablet-mobile-info-title-unique">남성</h4>
                  <p className="ancestral-tablet-mobile-info-text-unique">
                    남자의 지방에는 제사 대상의 관직명이 표시됩니다. <br /> 아래에서 선택한 광직명이 선택하신 기본 양식에 적용되어 출력됩니다.
                  </p>
                </div>

                <div className="ancestral-tablet-mobile-radio-options-unique">
                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-male"
                      value="student"
                      className="ancestral-tablet-mobile-radio-input-unique"
                      defaultChecked
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      학생 - 일반적으로 가장 널리쓰임
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-male"
                      value="professional"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      처사 - 전문직 종사자(회계사, 변리사등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-male"
                      value="legislator"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      의원 - 정치인(국회의원, 자치단체의원)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-male"
                      value="chairman"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      회장 - 법인/단체의 장(00협회장,00회장 등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-male"
                      value="judge"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      법관 - 법조인의 경우 사용(판, 검사, 변호사 등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-male"
                      value="scholar"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      학자 - 학계에 계셨던 분(교수, 박사, 학자 등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-male"
                      value="military"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      군관 - 군인의 경우 사용(장군, 고위장교 등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-male"
                      value="executive"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      임원 - 대기업 고위직의 경우(대기업 회장, 사장, 간부 등)
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Female-only subjects - matches great-great-grandmother, great-grandmother, grandmother, granduncle-wife, mother, wife, elder-brother-wife, younger-brother-wife */}
            {['고조모', '증조모', '조모', '백모', '모', '처', '형수', '제수'].includes(selectedAncestralSubject) && (
              <div className="ancestral-tablet-mobile-female-section-unique">
                <div className="ancestral-tablet-mobile-info-box-unique">
                  <h4 className="ancestral-tablet-mobile-info-title-unique">여성</h4>
                  <p className="ancestral-tablet-mobile-info-text-unique">
                    제사 대상이 여성일 경우 본관과 성씨가 입력됩니다. <br /> 본관 성씨를 빈칸에 한글로 적으시면 선택하신 기본양식에 적용되어 출력됩니다.
                  </p>
                </div>

                <div className="ancestral-tablet-mobile-female-input-section-unique">
                  <label className="ancestral-tablet-mobile-female-radio-option-unique">
                    <input
                      type="radio"
                      name="female-input-single"
                      value="bon-gwan"
                      className="ancestral-tablet-mobile-female-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-female-radio-label-unique">
                      본관 성씨 입력
                    </span>
                  </label>

                  <div className="ancestral-tablet-mobile-female-input-container-unique">
                    <input
                      type="text"
                      className="ancestral-tablet-mobile-female-input-field-unique"
                      placeholder="예) 김해김, 경주김"
                      value={inputValue}
                      onChange={(e) => handleTextInputChange(e.target.value)}
                    />
                    <button className="ancestral-tablet-mobile-female-search-button-unique" onClick={() => handleLastNameCheck()}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Mixed subjects - matches great-grandparents, grandparents, granduncle-aunt, parents, younger-sibling */}
            {['증조부모', '조부모', '백부모', '부모', '동생'].includes(selectedAncestralSubject) && (
              <div className="ancestral-tablet-mobile-gender-section-unique">
                <div className="ancestral-tablet-mobile-text-container-unique">
                  <h3 className="ancestral-tablet-mobile-content-title-unique">제사 대상 성별</h3>
                </div>

                <div className="ancestral-tablet-mobile-info-box-unique">
                  <h4 className="ancestral-tablet-mobile-info-title-unique">남성</h4>
                  <p className="ancestral-tablet-mobile-info-text-unique">
                    남자의 지방에는 제사 대상의 관직명이 표시됩니다. <br /> 아래에서 선택한 광직명이 선택하신 기본 양식에 적용되어 출력됩니다.
                  </p>
                </div>

                <div className="ancestral-tablet-mobile-radio-options-unique">
                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-mixed"
                      value="student"
                      className="ancestral-tablet-mobile-radio-input-unique"
                      defaultChecked
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      학생 - 일반적으로 가장 널리 쓰임
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-mixed"
                      value="chairman"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      회장 - 법인/단체의 장(00협회장,00회장 등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-mixed"
                      value="military"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      군관 - 군인의 경우 사용(장군, 고위장교 등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-mixed"
                      value="professional"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      처사 - 전문직 종사자(회계사, 변리사등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-mixed"
                      value="judge"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      법관 - 법조인의 경우 사용(판, 검사, 변호사 등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-mixed"
                      value="executive"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      임원 - 대기업 고위직의 경우(대기업 회장, 사장, 간부 등)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-mixed"
                      value="legislator"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      의원 - 정치인(국회의원, 자치단체의원)
                    </span>
                  </label>

                  <label className="ancestral-tablet-mobile-radio-option-unique">
                    <input
                      type="radio"
                      name="official-title-mixed"
                      value="scholar"
                      className="ancestral-tablet-mobile-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-radio-label-unique">
                      학자 - 학계에 계셨던 분(교수, 박사, 학자 등)
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Female sections for mixed subjects that need both male and female */}
            {['증조부모', '조부모', '백부모', '부모', '동생'].includes(selectedAncestralSubject) && (
              <div className="ancestral-tablet-mobile-female-section-unique">
                <div className="ancestral-tablet-mobile-info-box-unique">
                  <h4 className="ancestral-tablet-mobile-info-title-unique">여성</h4>
                  <p className="ancestral-tablet-mobile-info-text-unique">
                    제사 대상이 여성일 경우 본관과 성씨가 입력됩니다. <br /> 본관 성씨를 빈칸에 한글로 적으시면 선택하신 기본양식에 적용되어 출력됩니다.
                  </p>
                </div>

                <div className="ancestral-tablet-mobile-female-input-section-unique">
                  <label className="ancestral-tablet-mobile-female-radio-option-unique">
                    <input
                      type="radio"
                      name="female-input-mixed"
                      value="bon-gwan"
                      className="ancestral-tablet-mobile-female-radio-input-unique"
                    />
                    <span className="ancestral-tablet-mobile-female-radio-label-unique">
                      본관 성씨 입력
                    </span>
                  </label>

                  <div className="ancestral-tablet-mobile-female-input-container-unique">
                    <input
                      type="text"
                      className="ancestral-tablet-mobile-female-input-field-unique"
                      placeholder="예) 김해김, 경주김"
                      value={inputValue}
                      onChange={(e) => handleTextInputChange(e.target.value)}
                    />
                    <button className="ancestral-tablet-mobile-female-search-button-unique" onClick={() => handleLastNameCheck()}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Action Button */}
            <div className="ancestral-tablet-mobile-action-section-unique">
              <div style={{ marginBottom: '12px', padding: '0 16px' }}>
                <label htmlFor="orientation-select-mobile" style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>용지 방향:</label>
                <select 
                  id="orientation-select-mobile"
                  value={orientation} 
                  onChange={(e) => setOrientation(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#374151',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer'
                  }}
                >
                  <option value="landscape">가로 (Landscape)</option>
                  <option value="portrait">세로 (Portrait)</option>
                </select>
              </div>
              <button className="ancestral-tablet-mobile-print-button-unique" onClick={handlePrintAncestralTablet}>
                지방출력
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Print Preview Modal */}
      <PrintPreviewModal
        isOpen={showPrintPreview}
        onClose={() => setShowPrintPreview(false)}
        printData={null}
      />
    </div>
  );
};

export default AncestralTabletMobile;
